// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

// Loading spinner component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#0a0a0a'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid rgba(255, 255, 255, 0.1)',
      borderTopColor: '#3b82f6',
      borderRadius: '50%',
      animation: 'authSpinner 1s linear infinite'
    }} />
    <style>{`
      @keyframes authSpinner {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [readerAccess, setReaderAccess] = useState(() => {
    const stored = localStorage.getItem('readerAccess');
    return stored ? JSON.parse(stored) : null;
  });

  // Check if email is a writer email (@avenir.com.ai)
  const isWriterEmail = (email) => {
    return email && email.endsWith('@avenir.com.ai');
  };

  // ─── Sign up — anyone can create an account ───
  // Writers (@avenir.com.ai) get a writers/ doc → isWriter = true
  // Everyone else is a reader — they can log in, read gated content, etc.
  const signup = async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });

    if (isWriterEmail(email)) {
      // Writer account — store in writers collection
      await setDoc(doc(db, 'writers', result.user.uid), {
        email,
        displayName,
        createdAt: new Date().toISOString(),
        bio: '',
        avatar: ''
      });
    }

    // Store in readers collection too (for newsletter, analytics, etc.)
    const trimmed = email.trim().toLowerCase();
    const readerId = trimmed.replace(/[.@]/g, '_');
    await setDoc(doc(db, 'readers', readerId), {
      email: trimmed,
      name: displayName,
      createdAt: new Date().toISOString(),
      newsletter: true,
      subscribedAt: new Date().toISOString(),
    }, { merge: true });

    // Grant reader access immediately so gated content unlocks
    const readerData = {
      name: displayName,
      email: trimmed,
      grantedAt: new Date().toISOString(),
    };
    localStorage.setItem('readerAccess', JSON.stringify(readerData));
    setReaderAccess(readerData);

    return result;
  };

  // ─── Login — any Firebase Auth user ───
  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);

    // Automatically grant reader access on login so gated content unlocks
    if (!readerAccess) {
      const trimmed = email.trim().toLowerCase();
      const readerData = {
        name: result.user.displayName || email.split('@')[0],
        email: trimmed,
        grantedAt: new Date().toISOString(),
      };
      localStorage.setItem('readerAccess', JSON.stringify(readerData));
      setReaderAccess(readerData);
    }

    return result;
  };

  // Logout
  const logout = () => {
    return signOut(auth);
  };

  // Grant reader access (for non-authenticated users via ReaderGate email form)
  const grantReaderAccess = async (name, email, company, newsletter = true) => {
    const readerData = {
      name,
      email,
      company,
      newsletter,
      subscribedAt: newsletter ? new Date().toISOString() : null,
      grantedAt: new Date().toISOString()
    };

    try {
      await setDoc(doc(db, 'readers', email.replace(/[.@]/g, '_')), readerData, { merge: true });
    } catch (error) {
      console.error('Error storing reader data:', error);
    }

    localStorage.setItem('readerAccess', JSON.stringify(readerData));
    setReaderAccess(readerData);

    return readerData;
  };

  // Check if user has reader access — authenticated users always do
  const hasReaderAccess = () => {
    if (currentUser) return true;
    return readerAccess !== null;
  };

  // Clear reader access
  const clearReaderAccess = () => {
    localStorage.removeItem('readerAccess');
    setReaderAccess(null);
  };

  // Minimum 1000ms loading time for smooth UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        if (isWriterEmail(user.email)) {
          try {
            const writerDoc = await getDoc(doc(db, 'writers', user.uid));
            if (writerDoc.exists()) {
              setUserProfile(writerDoc.data());
            }
          } catch (error) {
            console.error('Error fetching writer profile:', error);
          }
        } else {
          setUserProfile(null);
        }

        // Read directly from localStorage to avoid stale closure
        // without needing readerAccess in the dep array
        const existingAccess = localStorage.getItem('readerAccess');
        if (!existingAccess) {
          const readerData = {
            name: user.displayName || user.email?.split('@')[0] || 'Reader',
            email: user.email,
            grantedAt: new Date().toISOString(),
          };
          localStorage.setItem('readerAccess', JSON.stringify(readerData));
          setReaderAccess(readerData);
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const value = {
    currentUser,
    userProfile,
    isWriter: currentUser && isWriterEmail(currentUser.email),
    readerAccess,
    hasReaderAccess,
    signup,
    login,
    logout,
    grantReaderAccess,
    clearReaderAccess,
    isWriterEmail
  };

  // Show spinner until both auth is resolved AND minimum time has elapsed
  const isLoading = loading || !minTimeElapsed;

  return (
    <AuthContext.Provider value={value}>
      {isLoading ? <LoadingSpinner /> : children}
    </AuthContext.Provider>
  );
};